import React from 'react';

import CheckboxListFilter, { Option } from '../../../components/Filters/CheckboxListFilter';

import { TFunction } from 'i18next';
import { withTranslation } from 'react-i18next';
import { FilterProps } from './FilterProps';

const CARD_BRAND_NULL = 'null';
const PAYEX_CREDITCARD = 'payex.creditcard';
const NETAXEPT_CREDITCARD = 'netaxept.creditcard';
const BAMBORA_CREDITCARD = 'bambora.creditcard';

export interface PaymentProductFilterProps extends FilterProps {
    enabledPaymentProductTypes: string[];
}

const getPaymentMethodName = (t: TFunction, value: string) => {
    if ([CARD_BRAND_NULL, PAYEX_CREDITCARD, NETAXEPT_CREDITCARD, BAMBORA_CREDITCARD].includes(value)) {
        return undefined;
    }
    const key = `payments.product_types.${value || 'undefined'}`;
    const translated = t(key);
    if (translated === key) {
        return value;
    }
    return translated;
};

const vippsPaymentProducts = ['bambora.vipps', 'payex.vipps', 'vipps'];
const swishPaymentProducts = ['bambora.swish', 'payex.swish', 'swish.swish'];
const mobilepayPaymentProducts = ['payex.mobilepay', 'bambora.mobilepay'];

const reducePaymentTypes = (values: string[], replace: string[], addIfReplaced: string) => {
    const filtered = values.filter((x) => !replace.includes(x));
    const shouldAdd = values.length > filtered.length;
    return [...filtered, ...(shouldAdd ? [addIfReplaced] : [])];
};

const transformForLabel = (values: string[]) => {
    const transformers = [
        { replace: vippsPaymentProducts, addIfReplaced: 'vipps' },
        { replace: swishPaymentProducts, addIfReplaced: 'swish.swish' },
        { replace: mobilepayPaymentProducts, addIfReplaced: 'payex.mobilepay' },
    ];
    const result = transformers.reduce<string[]>(
        (acc, t) => reducePaymentTypes(acc, t.replace, t.addIfReplaced),
        values
    );
    return result;
};

const StateFilter = (
    {
        queryParams,
        t,
        onChange,
        enabledPaymentProductTypes,
    }: PaymentProductFilterProps
) => {
    const cardBrandOptionValues =
        enabledPaymentProductTypes.includes('netaxept.creditcard') ||
        enabledPaymentProductTypes.includes('payex.creditcard') ||
        enabledPaymentProductTypes.includes('bambora.creditcard')
            ? ['Visa', 'MasterCard']
            : [];

    const vippsOptionValues = vippsPaymentProducts.find((p) => enabledPaymentProductTypes.includes(p))
        ? ['vipps']
        : [];
    const swishOptionValues = swishPaymentProducts.find((p) => enabledPaymentProductTypes.includes(p))
        ? ['swish.swish']
        : [];
    const mobilepayOptionValues = mobilepayPaymentProducts.find((p) => enabledPaymentProductTypes.includes(p))
        ? ['payex.mobilepay']
        : [];

    const paymentProductTypeOptionValues = [
        'dintero.zero',
        'payex.applepay',
        'payex.googlepay',
        'payex.clicktopay',
        'instabank.invoice',
        'instabank.finance',
        'instabank.installment',
        'instabank.postponement',
        'collector.invoice',
        'collector.invoice_b2b',
        'collector.finance',
        'collector.installment',
        'santander.debit_account',
        'klarna.klarna',
        'klarna.billie',
    ].filter((option) => enabledPaymentProductTypes.includes(option));
    const options: Option[] = [
        ...cardBrandOptionValues.map((value) => ({ value, label: value })),
        ...vippsOptionValues.map((value) => ({ value, label: t(`payments.product_types.${value}`) })),
        ...swishOptionValues.map((value) => ({ value, label: t(`payments.product_types.${value}`) })),
        ...mobilepayOptionValues.map((value) => ({ value, label: t(`payments.product_types.${value}`) })),
        ...paymentProductTypeOptionValues.map((value) => ({ value, label: t(`payments.product_types.${value}`) })),
    ];

    const configuredCreditCardPaymentTypes = enabledPaymentProductTypes.filter((x) =>
        [PAYEX_CREDITCARD, NETAXEPT_CREDITCARD, BAMBORA_CREDITCARD].includes(x));

    const configuredVippsPaymentTypes = enabledPaymentProductTypes.filter((x) => vippsPaymentProducts.includes(x));
    const configuredSwishPaymentTypes = enabledPaymentProductTypes.filter((x) => swishPaymentProducts.includes(x));
    const configuredMobilepayPaymentTypes = enabledPaymentProductTypes.filter((x) => mobilepayPaymentProducts.includes(x));

    const paymentProductTypesSelectedValues = queryParams.getAll('payment_product_type');
    const cardBrandSelectedValues = queryParams.getAll('card_brand');

    const values = [...paymentProductTypesSelectedValues, ...cardBrandSelectedValues];
    // removing the implicit no null for card_brand and payex.credit_card
    const labelValues = transformForLabel(values);

    const selectionCount = labelValues.filter(
        (v) => ![CARD_BRAND_NULL, PAYEX_CREDITCARD, NETAXEPT_CREDITCARD, BAMBORA_CREDITCARD].includes(v)
    ).length;

    return (
        <CheckboxListFilter
            label={t('payments.filter.payment_product_type_card_brand.label', {
                count: selectionCount,
                value: labelValues
                    .map((value) => getPaymentMethodName(t, value))
                    .filter((x) => x)
                    .join(', '),
            })}
            title={t('payments.filter.payment_product_type_card_brand.title')}
            name="payment_product_type_card_brand"
            values={values}
            options={options}
            onApply={(name, values) => {
                const cardBrandSelected = values.filter((v) => cardBrandOptionValues.includes(v));
                const nonCardBrandSelected = values.filter((v) => !cardBrandOptionValues.includes(v));
                const paymentProductTypeSelected = values.filter((v) => paymentProductTypeOptionValues.includes(v));
                if (cardBrandSelected.length > 0 && nonCardBrandSelected.length > 0) {
                    // Adds actual payment types for the credit card payments
                    paymentProductTypeSelected.push(...configuredCreditCardPaymentTypes);
                    // Adds support for payments without card brand (eg. the non card payment types)
                    cardBrandSelected.push(CARD_BRAND_NULL);
                }
                const vippsSelected = values.filter((v) => vippsOptionValues.includes(v));
                if (vippsSelected.length > 0) {
                    paymentProductTypeSelected.push(...configuredVippsPaymentTypes);
                }
                const swishSelected = values.filter((v) => swishOptionValues.includes(v));
                if (swishSelected.length > 0) {
                    paymentProductTypeSelected.push(...configuredSwishPaymentTypes);
                }
                const mobilepaySelected = values.filter((v) => mobilepayOptionValues.includes(v));
                if (mobilepaySelected.length > 0) {
                    paymentProductTypeSelected.push(...configuredMobilepayPaymentTypes);
                }

                onChange({
                    card_brand: cardBrandSelected,
                    payment_product_type: paymentProductTypeSelected,
                });
            }}
            onDelete={() => {
                onChange({
                    payment_product_type: [],
                    card_brand: [],
                });
            }}
        />
    );
};

export default withTranslation()(StateFilter);
